import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { methodologyService, GuidedMethodology, auth } from "../../lib/firebase";
import { getAuth, onAuthStateChanged } from "firebase/auth";

export const GuidedMethodologiesPage = (): JSX.Element => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Check if this is a new session from the URL query parameters
  const searchParams = new URLSearchParams(location.search);
  const isNewSession = searchParams.get('newSession') === 'true';
  
  // Add debug logging for the newSession parameter
  console.log(`GuidedMethodologiesPage - isNewSession: ${isNewSession}, search: ${location.search}`);
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [methodologies, setMethodologies] = useState<GuidedMethodology[]>([]);
  const [userName, setUserName] = useState<string>("");
  const authInstance = getAuth();
  
  // Function to navigate with preserving newSession parameter
  const navigateWithSessionParam = (path: string) => {
    if (isNewSession) {
      navigate(`${path}?newSession=true`);
    } else {
      navigate(path);
    }
  };

  // Authentication effect
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(authInstance, (user) => {
      if (user) {
        const displayName = user.displayName || user.email?.split('@')[0] || 'User';
        setUserName(displayName);
        localStorage.setItem('rawpa_user_name', displayName);
      } else {
        navigate('/signin');
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  // Load guided methodologies
  useEffect(() => {
    const loadMethodologies = async () => {
      setLoading(true);
      
      try {
        const data = await methodologyService.getGuidedMethodologies();
        // Filter to only show active methodologies to users
        setMethodologies(data.filter(m => m.status === 'Active'));
      } catch (error) {
        console.error("Error loading guided methodologies:", error);
        setError("Failed to load guided methodologies");
      } finally {
        setLoading(false);
      }
    };

    loadMethodologies();
  }, []);

  // Handle back button navigation based on current path
  const handleBackNavigation = () => {
    navigateWithSessionParam('/dashboard');
  };
  
  // Determine the path for methodology details based on current path
  const getMethodologyPath = (methodologyId: string) => {
    // If we're at /methodology/guided, use that path structure
    if (location.pathname === '/methodology/guided') {
      return `/methodology/guided/${methodologyId}`;
    }
    // Otherwise use the standard path
    return `/guided-methodologies/${methodologyId}`;
  };

  const handleSignOut = async () => {
    try {
      await authInstance.signOut();
      navigate('/signin');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  return (
    <div className="min-h-screen w-full bg-[#e1dcdc75] relative">
      {/* Navigation bar */}
      <div className="fixed w-full px-4 top-[46px] z-10">
        <div className="max-w-[1257px] mx-auto h-[77px] relative rounded-[28px]">
          <div className="absolute w-full h-full top-0 left-0 bg-white rounded-[28px] shadow-[0px_4px_4px_#00000040] blur-[3.05px] opacity-[0.53]" />
          <div className="relative h-full flex items-center justify-between px-6">
            <div className="flex items-center gap-4 cursor-pointer" onClick={() => navigate('/dashboard')}>
              <img className="w-[27px] h-[27px] object-cover" alt="Beetle" src="/beetle.png" />
              <span className="font-['Space_Grotesk'] font-bold text-black text-[32px]">RAWPA</span>
            </div>
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                onClick={() => navigate('/dashboard')}
                className="font-['Space_Grotesk'] text-black hidden lg:flex"
              >
                Back to Dashboard
              </Button>
              <div 
                onClick={handleSignOut}
                className="w-[48px] h-[48px] bg-[#daed6e] rounded-[12px] flex items-center justify-center cursor-pointer hover:bg-[#c5d85c] transition-colors"
              >
                <img className="w-6 h-6" alt="User" src="/user-icon.svg" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="container mx-auto px-4 pt-[160px] pb-16">
        {error && (
          <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded mb-4">
            <p>{error}</p>
          </div>
        )}
        
        <div className="flex items-center gap-2 mb-6">
          <button 
            onClick={handleBackNavigation}
            className="text-black opacity-60 hover:opacity-100 transition-opacity"
          >
            ← Back to Dashboard
          </button>
        </div>
        
        <div className="bg-white rounded-[20px] p-8 shadow-lg">
          <div className="mb-8">
            <h1 className="font-['Space_Grotesk'] font-bold text-2xl text-black mb-2">
              Guided Methodologies
            </h1>
            <p className="font-['Space_Grotesk'] text-sm text-black opacity-80">
              Follow interactive step-by-step guided methodologies for your security assessments
            </p>
          </div>
          
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="loader"></div>
            </div>
          ) : methodologies.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>No guided methodologies available at the moment</p>
              <p className="text-sm mt-2">Please check back later or contact an administrator</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {methodologies.map((methodology) => (
                <div 
                  key={methodology.id}
                  className="bg-white p-6 rounded-[20px] border border-[#E7E7E7] shadow-sm hover:shadow-md transition-shadow"
                >
                  <h3 className="font-['Space_Grotesk'] font-bold text-lg text-black mb-2">
                    {methodology.name}
                  </h3>
                  <p className="text-sm text-black opacity-80 mb-4 line-clamp-2">
                    {methodology.description}
                  </p>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-500">
                      {methodology.steps.length} steps
                    </span>
                    <Button 
                      className="h-9 px-4 bg-[#daed6e] rounded-lg hover:bg-[#c5d85c] transition-colors"
                      onClick={() => navigateWithSessionParam(getMethodologyPath(methodology.id))}
                    >
                      <span className="font-['Space_Grotesk'] font-medium text-black text-sm">
                        Start
                      </span>
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}; 